import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SwarmType, NotificationTypeFieldsFragment } from '@/graphql/types';
import { SwarmIconClasses } from '../../../../enums';
import { NotificationBorderVariants } from '../enums';
import InformationTooltip from '../../../reactForm/InformationTooltip';
import { useHover } from '../utils';

interface NotificationDetailProps {
    notification: NotificationTypeFieldsFragment;
}

function getSwarmIconClass(swarmType: SwarmType): string {
    return SwarmIconClasses[swarmType];
}

function NotificationDetail({ notification }: NotificationDetailProps): JSX.Element {
    const { level, swarmType, data, isLong, isLegacy } = notification;
    const { t } = useTranslation();
    const [showDescription, setShowDescription] = useState(false);
    const [isHovering, hoverProps] = useHover();
    const borderClass = useMemo(() => NotificationBorderVariants[level], [level]);
    const isExpanded = showDescription && isLong;
    const swarmIconClass = getSwarmIconClass(swarmType);

    const tooltip = isLegacy && isHovering && (
        <InformationTooltip
            information={gettext('LEGACY_NOTIFICATION_INFO')}
            className="text-form-text pl-2"
        />
    );

    function getHighlightClass(item: string): string {
        if (item.includes(`(${t('NEW')})`)) return 'bg-green-200';
        if (item.includes(`(${t('REMOVED')})`)) return 'bg-red-200';
        if (item.includes(`(${t('CHANGED')})`)) return 'bg-yellow-200';
        return '';
    }

    function renderExpandedContent(): JSX.Element {
        return (
            <div>
                {data.split('\n').map((item, idx) => (
                    <div key={idx}>
                        <span
                            className={getHighlightClass(item)}
                            dangerouslySetInnerHTML={{ __html: item }}
                        />
                        {idx === 0 && tooltip}
                        <br />
                    </div>
                ))}
            </div>
        );
    }

    function renderCollapsedContent(): JSX.Element {
        return (
            <>
                <span>{data.split('\n')[0]}</span>
                <small className="pl-2">{gettext('CLICK_FOR_MODE_DETAILS')}</small>
                {tooltip}
            </>
        );
    }

    function renderContent(): JSX.Element {
        if (!isLong) {
            return (
                <>
                    <span>{data}</span>
                    {tooltip}
                </>
            );
        }
        return showDescription ? renderExpandedContent() : renderCollapsedContent();
    }

    return (
        <div
            className="relative"
            onClick={() => setShowDescription((oldState) => !oldState)}
            {...hoverProps}
        >
            <div
                className={`flex gap-2 border-l-4 p-2.5 ${borderClass} ${
                    !showDescription && isLong ? 'collapsed' : ''
                }`}
            >
                <div className="w-8 shrink-0 md:w-[2%]">
                    <div
                        className={`h-8 w-full ${swarmIconClass} bg-contain bg-center bg-no-repeat text-center text-8xl leading-7 text-gray-700`}
                    />
                </div>
                <div
                    className={`min-w-0 flex-1 text-left ${
                        isExpanded ? '' : 'flex min-h-8 items-center'
                    }`}
                >
                    <div className="break-words">{renderContent()}</div>
                </div>
            </div>
        </div>
    );
}

export default NotificationDetail;
